import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import BankTermsDialog from "./bank/BankTermsDialog";
import { useAuth } from "@frontegg/react";
import { GET_USER } from "../../graphql/user";
import useAddHostedBankAccount from "../../hooks/useHostedBankAccount";
import useAcceptTerms from "../../hooks/useAcceptTerms";
import LoadingModal from "../common/LoadingModal";
import AccountType from "enums/AccountType";

interface BankIntegrationProps {
  accountType?: AccountType;
  renderTrigger: (
    handleOpenAddBank: () => void,
    loading: boolean,
  ) => React.ReactNode;
  onBankAdded?: (accountId: string) => void;
  refetchBankAccounts: () => void;
}

const BankIntegration: React.FC<BankIntegrationProps> = ({
  accountType = AccountType.BANK,
  renderTrigger,
  onBankAdded,
  refetchBankAccounts,
}) => {
  const { user } = useAuth();
  const [openTerms, setOpenTerms] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [accountKey, setAccountKey] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const { data: userData, loading: loadingUser } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const { addHostedBankAccount, loadingBank } = useAddHostedBankAccount(
    userData,
    setClientSecret,
    setAccountKey,
    setPaymentMethod,
    setOpenTerms,
  );

  const { handleAcceptTerms, savingHostedBankAccount } = useAcceptTerms(
    accountKey,
    clientSecret,
    async () => {
      await refetchBankAccounts();
      if (onBankAdded) onBankAdded(paymentMethod);
    },
    accountType,
  );

  async function handleOpenAddBank() {
    await addHostedBankAccount();
  }

  return (
    <>
      {renderTrigger(handleOpenAddBank, loadingBank || loadingUser)}
      <BankTermsDialog
        open={openTerms}
        onClose={async (accepted: boolean) => {
          setOpenTerms(false);
          if (accepted) {
            await handleAcceptTerms();
            await refetchBankAccounts();
          }
        }}
      />
      <LoadingModal
        open={loadingBank || loadingUser || savingHostedBankAccount}
      />
    </>
  );
};

export default BankIntegration;
